import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { dictionary, localizePath } from "@/i18n/translations";
import { api } from "@/lib/api";
import { setDocumentMeta } from "@/lib/seo";

export default function RegisterPage({ lang }) {
  const copy = dictionary[lang];

  const [form, setForm] = useState({
    full_name: "",
    email: "",
    password: "",
    invite_code: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");

  useEffect(() => {
    setDocumentMeta({
      lang,
      title: copy.seo.registerTitle,
      description: copy.seo.registerDescription,
    });
  }, [copy.seo.registerDescription, copy.seo.registerTitle, lang]);

  const onSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);

    try {
      const payload = {
        ...form,
        invite_code: form.invite_code || null,
      };
      const response = await api.post("/auth/register", payload);

      const pending = !response.data?.is_approved;
      const message = pending ? copy.auth.pending : copy.auth.approved;
      setResponseMessage(message);
      toast.success(message);

      setForm({
        full_name: "",
        email: "",
        password: "",
        invite_code: "",
      });
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail || copy.common.requestFailed);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page-auth" data-testid="register-page-root">
      <div className="flex w-full max-w-xl flex-col gap-4">
        <Link to={localizePath(lang)} data-testid="register-page-home-link">
          <Button
            variant="outline"
            className="rounded-full border-gold-500/30 bg-white/80 text-stone-800 hover:bg-white"
            data-testid="register-page-home-button"
          >
            {copy.nav.home}
          </Button>
        </Link>

        <Card className="w-full border-gold-500/20 shadow-xl" data-testid="register-page-card">
          <CardHeader data-testid="register-page-card-header">
            <CardTitle className="text-3xl" data-testid="register-page-title">{copy.auth.registerTitle}</CardTitle>
            <p className="text-sm text-stone-600" data-testid="register-page-subtitle">{copy.auth.registerSubtitle}</p>
          </CardHeader>

          <CardContent data-testid="register-page-card-content">
            <form className="space-y-4" onSubmit={onSubmit} data-testid="register-page-form">
              <div className="space-y-2">
                <label className="text-sm text-stone-700" data-testid="register-page-name-label">{copy.auth.fullName}</label>
                <Input
                  value={form.full_name}
                  onChange={(event) => setForm((prev) => ({ ...prev, full_name: event.target.value }))}
                  required
                  minLength={2}
                  data-testid="register-page-name-input"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm text-stone-700" data-testid="register-page-email-label">{copy.auth.email}</label>
                <Input
                  value={form.email}
                  onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))}
                  type="email"
                  required
                  data-testid="register-page-email-input"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm text-stone-700" data-testid="register-page-password-label">{copy.auth.password}</label>
                <Input
                  value={form.password}
                  onChange={(event) => setForm((prev) => ({ ...prev, password: event.target.value }))}
                  type="password"
                  required
                  minLength={8}
                  data-testid="register-page-password-input"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm text-stone-700" data-testid="register-page-invite-label">{copy.auth.inviteCode}</label>
                <Input
                  value={form.invite_code}
                  onChange={(event) => setForm((prev) => ({ ...prev, invite_code: event.target.value }))}
                  data-testid="register-page-invite-input"
                />
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full rounded-full bg-stone-900 py-6 text-cream-50 hover:bg-stone-700"
                data-testid="register-page-submit-button"
              >
                {submitting ? copy.common.loading : copy.auth.submitRegister}
              </Button>
            </form>

            <p className="mt-4 text-xs text-stone-500" data-testid="register-page-bootstrap-note">
              {copy.auth.firstAdmin}
            </p>

            {responseMessage && (
              <p className="mt-3 rounded-xl bg-gold-100 p-3 text-sm text-gold-900" data-testid="register-page-response-message">
                {responseMessage}
              </p>
            )}

            <Link className="mt-5 block text-sm text-gold-700 underline" to={localizePath(lang, "/staff/login")} data-testid="register-page-login-link">
              {copy.auth.goToLogin}
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
